// src/component/PaymentPopup.js
import React from 'react';
import PropTypes from 'prop-types';
import { FaTimes, FaCreditCard, FaSpinner, FaExclamationTriangle } from 'react-icons/fa'; // Add icons

const PaymentPopup = ({
    isOpen,
    onClose,
    totalAmount,
    onConfirm, // This will trigger the payment process later
    isLoading = false, // Prop for loading state (will be used later)
    error = null      // Prop for error state (will be used later)
}) => {

    // Don't render anything if the popup isn't open
    if (!isOpen) {
        return null;
    }

    // Prevent clicks inside the modal from closing it
    const handleContentClick = (e) => {
        e.stopPropagation();
    };

    // Format the total amount for display
    const formattedTotal = typeof totalAmount === 'number'
        ? `₹${totalAmount.toFixed(2)}`
        : 'N/A'; // Handle cases where amount might not be ready

    return (
        // Modal Overlay
        <div style={styles.overlay} onClick={onClose} role="dialog" aria-modal="true" aria-labelledby="payment-popup-title">
            {/* Modal Content */}
            <div style={styles.modalContent} onClick={handleContentClick}>
                {/* Close Button */}
                <button style={styles.closeButton} onClick={onClose} aria-label="Close">
                    <FaTimes />
                </button>

                {/* Title */}
                <h2 id="payment-popup-title" style={styles.title}>
                    <FaCreditCard style={{ marginRight: '10px', verticalAlign: 'middle' }} />
                    Confirm Your Payment
                </h2>

                {/* Total Amount Display */}
                <div style={styles.totalSection}>
                    <span style={styles.totalLabel}>Total Amount:</span>
                    <span style={styles.totalAmountValue}>{formattedTotal}</span>
                </div>

                {/* Placeholder for Payment Method Selection (Optional Future Feature) */}
                {/* <div style={styles.paymentMethodSection}>
                    <p>Select Payment Method:</p>
                     Add radio buttons or dropdown later
                </div> */}

                {/* Error Display Area (for future API integration) */}
                {error && (
                    <div style={styles.errorDisplay}>
                        <FaExclamationTriangle style={{ marginRight: '5px' }}/> Error: {error}
                    </div>
                )}

                {/* Action Buttons */}
                <div style={styles.buttonContainer}>
                    <button
                        style={{ ...styles.button, ...styles.cancelButton }}
                        onClick={onClose}
                        disabled={isLoading} // Disable if loading later
                    >
                        Cancel
                    </button>
                    <button
                        style={{ ...styles.button, ...styles.confirmButton }}
                        onClick={onConfirm} // This calls the function passed from Cart.js
                        disabled={isLoading} // Disable if loading later
                    >
                        {isLoading ? (
                            <FaSpinner className="fa-spin" style={{ marginRight: '5px' }} />
                        ) : null}
                        {isLoading ? 'Processing...' : 'Confirm Payment'}
                    </button>
                </div>

                {/* Mock Payment Notice */}
                <p style={styles.mockNotice}>
                    (Note: This is currently a mock payment confirmation.)
                </p>
            </div>
        </div>
    );
};

// PropTypes for type checking (good practice)
PaymentPopup.propTypes = {
    isOpen: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    totalAmount: PropTypes.number.isRequired,
    onConfirm: PropTypes.func.isRequired,
    isLoading: PropTypes.bool,
    error: PropTypes.string,
};

// Basic Styles
const styles = {
    overlay: {
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.6)', // Darker overlay
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1050, // Ensure it's above other content
        backdropFilter: 'blur(3px)', // Optional blur effect
    },
    modalContent: {
        backgroundColor: '#fff',
        padding: '30px 40px', // More padding
        borderRadius: '10px',
        boxShadow: '0 5px 20px rgba(0, 0, 0, 0.25)',
        maxWidth: '450px', // Slightly wider
        width: '90%', // Responsive width
        position: 'relative', // For absolute positioning of close button
        textAlign: 'center',
        animation: 'fadeInScale 0.3s ease-out forwards', // Simple animation
    },
    closeButton: {
        position: 'absolute',
        top: '10px',
        right: '15px',
        background: 'none',
        border: 'none',
        fontSize: '1.5rem', // Larger icon
        color: '#888',
        cursor: 'pointer',
        padding: '5px',
        lineHeight: 1, // Ensure icon centers well
    },
    closeButtonHover: { // Can't do hover easily inline, use CSS classes for this
         color: '#333',
    },
    title: {
        marginTop: 0, // Remove default margin if close button is present
        marginBottom: '25px',
        color: '#1a237e', // Match theme
        fontSize: '1.6em',
        fontWeight: '600',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
    },
    totalSection: {
        margin: '20px 0 30px 0',
        padding: '15px',
        backgroundColor: '#f0f2f9', // Light background highlight
        borderRadius: '6px',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
    },
    totalLabel: {
        fontSize: '1.1em',
        color: '#555',
        fontWeight: '500',
    },
    totalAmountValue: {
        fontSize: '1.4em',
        fontWeight: 'bold',
        color: '#1a237e', // Match theme
    },
    errorDisplay: {
        backgroundColor: '#fff0f0',
        color: '#c00',
        border: '1px solid #fcc',
        borderRadius: '4px',
        padding: '10px 15px',
        marginBottom: '20px',
        textAlign: 'left',
        fontSize: '0.95em',
        display: 'flex',
        alignItems: 'center',
    },
    buttonContainer: {
        display: 'flex',
        justifyContent: 'space-between', // Space out buttons
        marginTop: '30px',
        gap: '15px', // Add gap between buttons
    },
    button: {
        padding: '12px 25px', // Larger buttons
        border: 'none',
        borderRadius: '6px',
        fontSize: '1em',
        fontWeight: '600',
        cursor: 'pointer',
        transition: 'background-color 0.2s ease, opacity 0.2s ease',
        flexGrow: 1, // Make buttons share space
        display: 'flex', // For icon alignment
        alignItems: 'center',
        justifyContent: 'center',
    },
    confirmButton: {
        backgroundColor: '#1a237e', // Theme color
        color: 'white',
    },
    cancelButton: {
        backgroundColor: '#f1f1f1', // Light grey
        color: '#555',
        border: '1px solid #ddd',
    },
    mockNotice: {
        marginTop: '25px',
        fontSize: '0.85em',
        color: '#777',
        fontStyle: 'italic',
    },
    // Simple fade-in/scale animation (add this keyframes rule to your global CSS or use a library)
    '@keyframes fadeInScale': {
        '0%': { opacity: 0, transform: 'scale(0.95)' },
        '100%': { opacity: 1, transform: 'scale(1)' },
    },
};

// Add this if you haven't already got a global spin animation
// (Alternatively, use inline style for FaSpinner if preferred)
const spinKeyframes = `
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
  .fa-spin {
    animation: spin 1s linear infinite;
  }
`;

// Inject keyframes (simple method, better to have in a global CSS file)
if (typeof document !== 'undefined') { // Check if running in browser
    const styleSheet = document.styleSheets[0];
    if (styleSheet) {
        // Check if keyframes already exist
        let ruleExists = false;
        try {
            for (let i = 0; i < styleSheet.cssRules.length; i++) {
                if (styleSheet.cssRules[i].name === 'spin') {
                    ruleExists = true; break;
                }
            }
        } catch (e) { console.warn("Could not access CSS rules for keyframes check."); }

        if (!ruleExists) {
             try { styleSheet.insertRule(spinKeyframes, styleSheet.cssRules.length); }
             catch (e) { console.error("Could not insert spin keyframes:", e); }
        }

        // Inject the modal animation too if needed globally
        try {
             let modalAnimExists = false;
             for (let i = 0; i < styleSheet.cssRules.length; i++) {
                 if (styleSheet.cssRules[i].name === 'fadeInScale') {
                     modalAnimExists = true; break;
                 }
             }
             if (!modalAnimExists) {
                 styleSheet.insertRule(`@keyframes fadeInScale { 0% { opacity: 0; transform: scale(0.95); } 100% { opacity: 1; transform: scale(1); } }`, styleSheet.cssRules.length);
             }
        } catch (e) { console.error("Could not insert modal animation keyframes:", e); }
    }
}

export default PaymentPopup;